/**
 * AI Tools API client — Settings → AI 도구 탭.
 *
 * chat-server proxy(/api/v1/admin/ai-tools*) → orchestrator-svc(/api/v1/ai/tools*).
 * 권한: ADMIN / SYSTEM_ADMIN (chat-server route 단 + orchestrator actor 헤더 검증).
 */

import { apiClient } from './client';

export interface AiToolMeta {
  name: string;
  description: string;
  required_role?: 'admin' | 'manager' | 'user';
  trust_class?: 'read' | 'write' | 'destructive';
  trust_level?: 'trusted' | 'standard' | 'untrusted';
  domain: string;
  tool_kind?: string;
  endpoint?: string;
  input_schema?: {
    type: 'object';
    properties: Record<string, unknown>;
    required?: string[];
  };
  _meta?: Record<string, unknown>;
}

export interface AiToolCacheInfo {
  domain: string;
  size: number;
  age_ms: number | null;
  ttl_ms: number;
}

export interface AiToolsListResult {
  total: number;
  role: 'admin' | 'manager' | 'user';
  tools: AiToolMeta[];
  by_domain: Record<string, AiToolMeta[]>;
  cache_stats: AiToolCacheInfo[];
}

export interface AiToolConnectionHttp {
  id: string;
  kind: 'http';
  label?: string;
  base_url: string;
  auth?: { type: 'none' | 'query' | 'header' | 'bearer'; key?: string; value_env?: string };
}
export interface AiToolConnectionSqlite {
  id: string;
  kind: 'sqlite';
  label?: string;
  path: string;
}
export interface AiToolConnectionSql {
  id: string;
  kind: 'postgres' | 'mysql';
  label?: string;
  dsn_env?: string;
  dsn?: string;
  ssl?: boolean;
  schema?: string;
}
export type AiToolConnection = AiToolConnectionHttp | AiToolConnectionSqlite | AiToolConnectionSql;
export type AiToolConnectionKind = AiToolConnection['kind'];

export interface SynthesizeResult {
  ok: true;
  proposed: AiToolMeta;
  rationale: string;
  validated: boolean;
  provider_id: string | null;
  model: string | null;
  latency_ms: number;
}

export async function listAiTools(): Promise<AiToolsListResult> {
  const { data } = await apiClient.get<AiToolsListResult>('/admin/ai-tools');
  // 방어 가드(additive) — tools/cache_stats 가 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
  return {
    ...data,
    tools: Array.isArray(data?.tools) ? data.tools : [],
    cache_stats: Array.isArray(data?.cache_stats) ? data.cache_stats : [],
  };
}

export async function refreshAiTools(): Promise<{ ok: boolean; note?: string }> {
  const { data } = await apiClient.post<{ ok: boolean; note?: string }>('/admin/ai-tools/refresh');
  return data;
}

export async function listAiToolConnections(): Promise<{ connections: AiToolConnection[] }> {
  const { data } = await apiClient.get<{ connections: AiToolConnection[] }>('/admin/ai-tools/connections');
  return { connections: Array.isArray(data?.connections) ? data.connections : [] };
}

export async function createAiToolConnection(
  conn: AiToolConnection,
): Promise<{ ok: boolean; connection: AiToolConnection }> {
  const { data } = await apiClient.post<{ ok: boolean; connection: AiToolConnection }>(
    '/admin/ai-tools/connections',
    conn,
  );
  return data;
}

export async function deleteAiToolConnection(id: string): Promise<{ ok: boolean; removed: number }> {
  const { data } = await apiClient.delete<{ ok: boolean; removed: number }>(
    `/admin/ai-tools/connections/${encodeURIComponent(id)}`,
  );
  return data;
}

export interface ConnectionTestResult {
  ok: boolean;
  status?: number;
  latency_ms?: number;
  tables?: string[];
  error?: string;
}
export async function testAiToolConnection(id: string): Promise<ConnectionTestResult> {
  const { data } = await apiClient.post<ConnectionTestResult>(
    `/admin/ai-tools/connections/${encodeURIComponent(id)}/test`,
  );
  return data;
}

export async function createCustomAiTool(tool: AiToolMeta): Promise<{ ok: boolean; tool: AiToolMeta }> {
  const { data } = await apiClient.post<{ ok: boolean; tool: AiToolMeta }>('/admin/ai-tools/custom', tool);
  return data;
}

export async function deleteCustomAiTool(name: string): Promise<{ ok: boolean; removed: number }> {
  const { data } = await apiClient.delete<{ ok: boolean; removed: number }>(
    `/admin/ai-tools/custom/${encodeURIComponent(name)}`,
  );
  return data;
}

export async function synthesizeAiTool(brief: string, connectionId: string): Promise<SynthesizeResult> {
  const { data } = await apiClient.post<SynthesizeResult>('/admin/ai-tools/synthesize', {
    brief,
    connection_id: connectionId,
  });
  return data;
}

// ─── DB 스키마 분석 ───────────────────────────────────────────

export interface DbColumn {
  name: string;
  type: string;
  nullable: boolean;
  pk: boolean;
}
export interface DbForeignKey {
  column: string;
  ref_table: string;
  ref_column: string;
}
export interface DbTable {
  name: string;
  columns: DbColumn[];
  primary_key: string[];
  foreign_keys: DbForeignKey[];
  approx_rows: number | null;
}
export interface DbSchema {
  engine: 'sqlite' | 'postgres' | 'mysql';
  schema?: string;
  table_count: number;
  truncated: boolean;
  tables: DbTable[];
}

export async function introspectConnection(id: string): Promise<DbSchema> {
  const { data } = await apiClient.post<DbSchema>(
    `/admin/ai-tools/connections/${encodeURIComponent(id)}/introspect`,
  );
  return { ...data, tables: Array.isArray(data?.tables) ? data.tables : [] };
}

// ─── AI 일괄 생성 ─────────────────────────────────────────────

export interface ToolCandidate {
  proposed: AiToolMeta;
  validated: boolean;
  validation_error?: string;
  duplicate?: boolean;
}
export interface SynthesizeBatchResult {
  ok: true;
  candidates: ToolCandidate[];
  schema_summary: { engine: string; table_count: number; truncated: boolean } | null;
  provider_id: string | null;
  model: string | null;
  latency_ms: number;
}

export async function synthesizeBatch(
  connectionId: string,
  opts?: { focus?: string; maxTools?: number },
): Promise<SynthesizeBatchResult> {
  const { data } = await apiClient.post<SynthesizeBatchResult>('/admin/ai-tools/synthesize-batch', {
    connection_id: connectionId,
    focus: opts?.focus,
    max_tools: opts?.maxTools,
  });
  return { ...data, candidates: Array.isArray(data?.candidates) ? data.candidates : [] };
}

export interface BatchSaveResult {
  ok: boolean;
  added: string[];
  skipped: Array<{ name: string; reason: string }>;
}
export async function createCustomAiToolsBatch(tools: AiToolMeta[]): Promise<BatchSaveResult> {
  const { data } = await apiClient.post<BatchSaveResult>('/admin/ai-tools/custom/batch', { tools });
  // 방어 가드(additive) — added/skipped 가 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
  return {
    ...data,
    added: Array.isArray(data?.added) ? data.added : [],
    skipped: Array.isArray(data?.skipped) ? data.skipped : [],
  };
}

// ─── OpenAPI(Swagger) import ──────────────────────────────────

export interface OpenApiToolDraft {
  draft_id: string;
  enabled_default: boolean;
  method: 'get' | 'post' | 'put' | 'patch' | 'delete' | 'head' | 'options';
  path: string;
  spec: AiToolMeta;
  disabled_reason?: string;
}
export interface OpenApiPreview {
  error?: string;
  suggested_label?: string;
  inferred_base_url?: string;
  connection_draft: AiToolConnectionHttp;
  tools: OpenApiToolDraft[];
  stats: { total: number; by_method: Record<string, number>; auto_enabled: number };
  warnings: string[];
}

export async function openapiPreview(args: {
  connectionId: string;
  url?: string;
  spec?: string;
  baseUrlOverride?: string;
  labelOverride?: string;
}): Promise<OpenApiPreview> {
  const { data } = await apiClient.post<OpenApiPreview>('/admin/ai-tools/connections/openapi/preview', {
    connection_id: args.connectionId,
    source: args.url ? { url: args.url } : { spec: args.spec },
    base_url_override: args.baseUrlOverride,
    label_override: args.labelOverride,
  });
  // 방어 가드(additive) — tools/warnings 가 배열이 아닌 비정형 응답에도 소비처 map 크래시 차단.
  return {
    ...data,
    tools: Array.isArray(data?.tools) ? data.tools : [],
    warnings: Array.isArray(data?.warnings) ? data.warnings : [],
  };
}

export interface OpenApiSynthesizeResult {
  ok: boolean;
  enhancements: Record<string, { description?: string; recommended?: boolean }>;
  model: string | null;
  latency_ms: number;
}
export async function openapiSynthesize(
  drafts: Array<{ name: string; method: string; path: string; summary?: string }>,
  focus?: string,
): Promise<OpenApiSynthesizeResult> {
  const { data } = await apiClient.post<OpenApiSynthesizeResult>(
    '/admin/ai-tools/connections/openapi/synthesize',
    { drafts, focus },
  );
  return data;
}

export interface OpenApiImportResult {
  ok: boolean;
  connection: AiToolConnectionHttp;
  tools: AiToolMeta[];
  added: { connection: number; tools: number };
}
export async function openapiImport(args: {
  connectionId: string;
  baseUrl: string;
  label?: string;
  auth?: AiToolConnectionHttp['auth'];
  selectedTools: AiToolMeta[];
}): Promise<OpenApiImportResult> {
  const { data } = await apiClient.post<OpenApiImportResult>('/admin/ai-tools/connections/openapi/import', {
    connection_id: args.connectionId,
    base_url: args.baseUrl,
    label: args.label,
    auth: args.auth,
    selected_tools: args.selectedTools,
  });
  return { ...data, tools: Array.isArray(data?.tools) ? data.tools : [] };
}
